import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { publish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import { Topics, type Payload, type RovoRemixConsumer } from '@atlaskit/rovo-triggers/types';

import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';
import {
	PAGE_REMIX_OPTIONS,
	getTypeDisplayName,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import remixOverlayMessages from '@atlassian/malleable-ui-components/remix/remix-overlay/messages';
import { releaseRemixHandoffHold } from '@atlassian/malleable-ui-components/state/machine/remix-handoff-hold';
import type {
	RemixOptionSelectionParameters,
	RemixOptionKind,
} from '@atlassian/malleable-ui-components/remix/remix-overlay/types';
import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	abortRemixCreateExperience,
	abortRemixCreateExperienceIfNotCommitted,
	startRemixCreateExperience,
	succeedRemixCreateExperienceAtHandoff,
	succeedRemixCreateExperienceAtSidebarHandoff,
	trackRemixCreateOptionSelected,
} from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import type {
	EditorRemixLifecycleEndReason,
	EditorRemixLifecycleEvent,
	RemixContentComponentProps,
} from '@atlassian/malleable-ui-components/types';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { resolveRemixDynamicUiContentType, type DynamicUiSource } from './common-types.ts';
import { flattenDocContentForPageRemixFragment } from './flattenDocContentForPageRemixFragment.ts';
import {
	prepareObjectSidebarPageRemixStart,
	runObjectSidebarPageRemixNativeStart,
	type ObjectSidebarPageRemixStartRejectionReason,
} from './ObjectSidebarPageRemixStart.ts';
import {
	useObjectSidebarRemixLifecycleDiagnostics,
	type ObjectSidebarRemixLifecycleEntryPath,
} from './useObjectSidebarRemixLifecycleDiagnostics.ts';
import { useRemixThinkingBarProgress } from './useRemixThinkingBarProgress.ts';
import { useRemixGenerationAnalytics } from './useRemixGenerationAnalytics.ts';
import {
	useShouldHideAttachmentRemixOptions,
	useShouldHideDiagramRemixOption,
} from './useShouldHideDiagramRemixOption.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';
import type { ObjectSidebarRemixPhase } from './objectSidebarRemixParity.ts';
import {
	useObjectSidebarRemixLifecycle,
	type ObjectSidebarRemixLifecycleRead,
	type RetainedObjectSidebarRemixLifecycle,
} from './ObjectSidebarRemixLifecycle.tsx';
import {
	useObjectSidebarStagingTransfer,
	type ObjectSidebarStagingTransferLifecycle,
	type ObjectSidebarStagingTransferTerminal,
} from './useObjectSidebarStagingTransfer.ts';

const PAGE_REMIX_ENTRY_POINT = 'objectSidebar';
const PAGE_REMIX_SOURCE = 'remixModalFromObjectLevelSidebar';
export const PAGE_REMIX_CONSUME_ONCE_KEY = 'renderer-page-remix';
const EMPTY_PAGE_ADF: DocNode = { type: 'doc', version: 1, content: [] };

const publishPageRemixHandoffFailed = () => {
	publish(Topics.ROVO_REMIX, {
		type: 'rovo-remix-handoff-failed',
		source: PAGE_REMIX_CONSUME_ONCE_KEY,
	} as unknown as Payload);
};

/**
 * Entry path recorded for the `request-rovo-page-remix` compatibility event. Shared by its
 * observed and rejected diagnostics so rollout evidence attributes both to the same caller.
 */
const getRequestRovoPageRemixEntryPath = (payload: Payload): ObjectSidebarRemixLifecycleEntryPath =>
	(payload as { source?: unknown }).source === 'editor' ? 'editor' : 'unknown';

const isRequestRovoPageRemix = (payload: Payload): boolean =>
	(payload as { type: string }).type === 'request-rovo-page-remix';

const isEmptyPageAdf = (doc: DocNode | null | undefined): boolean => {
	if (!doc || doc.type !== 'doc') {
		return true;
	}
	const content = doc.content;
	if (!content || content.length === 0) {
		return true;
	}
	return content.every(
		(node) =>
			node.type === 'paragraph' &&
			(!('content' in node) || !node.content || node.content.length === 0),
	);
};

type PageRemixRequestParameters = RemixOptionSelectionParameters & { conversationId?: string };

type PreparedPageRemixRequest =
	| {
			status: 'started';
			params: PageRemixRequestParameters;
			lifecycleAdmission: PageRemixLifecycleAdmissionOutcome;
	  }
	| {
			status: 'rejected';
			reason: ObjectSidebarPageRemixStartRejectionReason;
	  };

type PageRemixStartEntryPath = 'popup-option' | 'rovo-sidebar';

/**
 * The Rovo open action this coordinator is currently armed against.
 *
 * `token` identifies one open action, so a submit accepted against it can be rejected once
 * Rovo has re-armed for a later one. `consumer` is the scope the submit has to match: the
 * Object Sidebar arms as `renderer`, but a selection-to-page scope update re-arms the same
 * open action under whichever consumer published it.
 */
type ArmedOpenAction = { token: number; consumer: RovoRemixConsumer };

type PageRemixSubmitContext = { isCurrentOpenAction: () => boolean };

// TODO: Consolidate with ListenForSelectedContentRemixSubmit. Both listen for a Rovo Remix
// submit, start the existing native Remix path once, and stand down if the user closes Rovo,
// starts a new chat, opens a different Remix entry, or navigates. They share that skeleton and
// should be one component, even though one handles the whole page and the other handles
// selected text.
/** Listens for whole-page Rovo Remix submit and starts the existing page remix path. */
const PageRemixSubmitListener = ({
	currentContentId,
	hasGenerationStarted,
	onAcceptSubmit,
	onDisarm,
	originContentIdRef,
	isRemixLifecycleTreatmentEnabled,
}: {
	currentContentId: string | undefined;
	hasGenerationStarted: boolean;
	onAcceptSubmit: (
		params: PageRemixRequestParameters,
		context: PageRemixSubmitContext,
	) => Promise<void>;
	onDisarm: () => void;
	originContentIdRef: React.MutableRefObject<string | undefined>;
	isRemixLifecycleTreatmentEnabled?: boolean;
}) => {
	const hasGenerationStartedRef = useRef(hasGenerationStarted);
	hasGenerationStartedRef.current = hasGenerationStarted;
	const acceptedSubmitRef = useRef(false);
	const armedOpenActionRef = useRef<ArmedOpenAction | undefined>(undefined);
	const lastOpenActionTokenRef = useRef(0);
	const onAcceptSubmitRef = useRef(onAcceptSubmit);
	onAcceptSubmitRef.current = onAcceptSubmit;
	const onDisarmRef = useRef(onDisarm);
	onDisarmRef.current = onDisarm;

	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const reportLifecycleDiagnosticRef = useRef(reportLifecycleDiagnostic);
	reportLifecycleDiagnosticRef.current = reportLifecycleDiagnostic;
	const isRemixLifecycleTreatmentEnabledRef = useRef(isRemixLifecycleTreatmentEnabled);
	isRemixLifecycleTreatmentEnabledRef.current = isRemixLifecycleTreatmentEnabled;
	const logRequestRovoPageRemixObserved = useCallback(
		(payload: Payload) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'requestRovoPageRemix',
				entryPath: getRequestRovoPageRemixEntryPath(payload),
			});
		},
		[reportLifecycleDiagnostic],
	);

	/**
	 * Records why a Rovo submit was refused before it could reach lifecycle admission or the
	 * native send. Treatment-only, matching the `requestRovoPageRemix` diagnostic, so control
	 * traffic adds no operational event volume.
	 */
	const reportRejectedSubmit = useCallback(
		(
			rejectionReason: ObjectSidebarPageRemixStartRejectionReason,
			entryPath: ObjectSidebarRemixLifecycleEntryPath,
		) => {
			if (!isRemixLifecycleTreatmentEnabledRef.current) {
				return;
			}
			reportLifecycleDiagnosticRef.current('rejected', {
				operation: 'validateRovoSubmitScope',
				entryPath,
				rejectionReason,
			});
		},
		[],
	);

	/**
	 * Stands the coordinator down.
	 *
	 * The armed action is cleared unconditionally, including while a submit is still in flight.
	 * Its token is what an accepted start re-checks before admission, so a close, reset, or scope
	 * change has to invalidate it or the Remix lands after the user has already moved on.
	 *
	 * Everything else only happens when nothing is in flight. `originContentIdRef` feeds the
	 * navigation guard for an admitted flow, so clearing it mid-flight would stop navigation from
	 * settling that flow.
	 */
	const disarm = useCallback(() => {
		armedOpenActionRef.current = undefined;

		if (hasGenerationStartedRef.current || acceptedSubmitRef.current) {
			return;
		}
		originContentIdRef.current = undefined;
		onDisarmRef.current();
	}, [originContentIdRef]);

	/**
	 * Arms this coordinator against a Rovo open action.
	 *
	 * `reuseArmedAction` keeps the token of the open action already in flight, so a
	 * selection-to-page scope update re-arms that same action rather than counting as a new
	 * one. A fresh `open-rovo-remix-sidebar` always mints a new token, which invalidates any
	 * submit still in flight for the previous action.
	 */
	const armOpenAction = useCallback(
		(consumer: RovoRemixConsumer, { reuseArmedAction }: { reuseArmedAction: boolean }) => {
			const armedToken = reuseArmedAction ? armedOpenActionRef.current?.token : undefined;
			if (armedToken === undefined) {
				lastOpenActionTokenRef.current += 1;
			}
			armedOpenActionRef.current = {
				token: armedToken ?? lastOpenActionTokenRef.current,
				consumer,
			};
			acceptedSubmitRef.current = false;
			originContentIdRef.current = currentContentId;
		},
		[currentContentId, originContentIdRef],
	);

	const acceptPageRequest = useCallback(
		(
			params: PageRemixRequestParameters,
			armedOpenAction: ArmedOpenAction | undefined,
			entryPath: ObjectSidebarRemixLifecycleEntryPath,
		) => {
			const originContentId = originContentIdRef.current;
			if (originContentId && currentContentId && originContentId !== currentContentId) {
				reportRejectedSubmit('stale-adf', entryPath);
				return;
			}

			if (acceptedSubmitRef.current || hasGenerationStartedRef.current) {
				reportRejectedSubmit('duplicate-submit', entryPath);
				return;
			}

			acceptedSubmitRef.current = true;
			// The compatibility path has no armed action to bind to, so it stays current for the
			// whole of its own start.
			const acceptedToken = armedOpenAction?.token;
			void onAcceptSubmitRef
				.current(params, {
					isCurrentOpenAction: () =>
						acceptedToken === undefined || armedOpenActionRef.current?.token === acceptedToken,
				})
				.catch(() => {
					acceptedSubmitRef.current = false;
				});
		},
		[currentContentId, originContentIdRef, reportRejectedSubmit],
	);

	useSubscribe(
		{ topic: Topics.ROVO_REMIX, consumeOnceKey: PAGE_REMIX_CONSUME_ONCE_KEY },
		(payload) => {
			if (payload.type === 'update-rovo-remix-preset') {
				if (payload.data.remixPreset === 'page') {
					// Rovo moved an open Remix onto page scope. The editor publishes this when the
					// user removes the selection context, so any consumer may re-arm here.
					armOpenAction(payload.data.remixConsumer, { reuseArmedAction: true });
				} else {
					disarm();
				}
				return;
			}

			if (isRequestRovoPageRemix(payload)) {
				const data = (payload as { data?: Record<string, unknown> }).data ?? {};
				const type = (data.remixOption ?? data.type) as RemixOptionKind | undefined;
				if (isRemixLifecycleTreatmentEnabled) {
					logRequestRovoPageRemixObserved(payload);
				}
				if (!type) {
					return;
				}
				acceptPageRequest(
					{
						type,
						subType: (data.remixSubType ?? data.subType) as string | undefined,
						userPrompt: data.userPrompt as string | undefined,
						conversationId: data.conversationId as string | undefined,
					},
					undefined,
					getRequestRovoPageRemixEntryPath(payload),
				);
				return;
			}

			// A selection-scoped submit belongs to the selected-content listener, so it is not
			// this coordinator's to refuse.
			if (payload.type !== 'submit-rovo-remix' || payload.data.remixPreset !== 'page') {
				return;
			}

			const armedOpenAction = armedOpenActionRef.current;
			if (!armedOpenAction) {
				reportRejectedSubmit('not-armed', 'rovo-sidebar');
				return;
			}

			if (payload.data.remixConsumer !== armedOpenAction.consumer) {
				reportRejectedSubmit('wrong-scope', 'rovo-sidebar');
				return;
			}

			// Rovo owns the conversation this submit continues, so its identity is required. A
			// submit without one has nothing for the handoff to be acknowledged against. Checked
			// for blankness but forwarded verbatim: the caller's identity is authoritative and
			// must never be silently rewritten.
			const conversationId = payload.data.conversationId;
			if (!conversationId?.trim()) {
				reportRejectedSubmit('missing-identity', 'rovo-sidebar');
				return;
			}

			acceptPageRequest(
				{
					type: payload.data.remixOption,
					subType: payload.data.remixSubType,
					userPrompt: payload.data.userPrompt,
					conversationId,
				},
				armedOpenAction,
				'rovo-sidebar',
			);
		},
	);

	useSubscribe({ topic: Topics.AI_MATE }, (payload) => {
		if (payload.type === 'chat-close') {
			disarm();
			return;
		}

		if (payload.type === 'chat-new' && payload.data.resetActiveMenu) {
			disarm();
			return;
		}

		if (payload.type !== 'open-rovo-remix-sidebar') {
			return;
		}

		// Only the Object Sidebar's own renderer + page open arms this coordinator. An editor
		// open, or a selection-scoped one, belongs to a different Remix entry point.
		if (payload.data.remixConsumer === 'renderer' && payload.data.remixPreset === 'page') {
			armOpenAction('renderer', { reuseArmedAction: false });
			return;
		}

		disarm();
	});

	useEffect(() => {
		const originContentId = originContentIdRef.current;
		if (originContentId && currentContentId && originContentId !== currentContentId) {
			disarm();
		}
	}, [currentContentId, disarm, originContentIdRef]);

	// Owner destruction disarms. An active flow is settled by the lifecycle owner's own
	// teardown, so this only has to make sure no armed action outlives the coordinator.
	useEffect(
		() => () => {
			armedOpenActionRef.current = undefined;
			originContentIdRef.current = undefined;
			onDisarmRef.current();
		},
		[originContentIdRef],
	);

	return null;
};

const PAGE_REMIX_OBJECT_CONTENT_TYPES: Partial<Record<RemixOptionKind, string>> = {
	slides: 'slides',
	whiteboard: 'whiteboard',
	database: 'database',
};

const getPageRemixObjectCreationContext = (type: RemixOptionKind) => {
	if (type === 'infographic') {
		return { experience: 'remix_object' as const, blocks: ['infographic'] };
	}

	const contentType = PAGE_REMIX_OBJECT_CONTENT_TYPES[type];
	return contentType
		? { experience: 'remix_object' as const, contentTypes: [contentType] }
		: undefined;
};

const constructPageDynamicUiSource = (
	adfDocument: DocNode,
	contentType: DynamicUiSource['contentType'],
): DynamicUiSource => ({
	contentType,
	startLocalId: '',
	endLocalId: '',
	startIndex: 0,
	endIndex: -1,
	fragmentAdf: flattenDocContentForPageRemixFragment(adfDocument),
	fullAdf: adfDocument,
});

/** Remix types that open the AIFC staging modal directly, skipping the thinking bar. */
const OPEN_AIFC_MODAL_TYPES = new Set(['slides', 'whiteboard', 'database']);

/**
 * Remix types whose flow never shows the thinking bar, because they hand off somewhere else
 * instead of waiting on a preview.
 *
 * Shared by the `skipThinkingTypes` prop and the Rovo submit path so the two cannot disagree
 * about which types show the bar. Rebuilt per call so it always reflects the current
 * experiment value.
 */
const getPageRemixSkipThinkingTypes = (): Set<string> =>
	new Set([
		...OPEN_AIFC_MODAL_TYPES,
		'infographic',
		...(expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true) ? [] : ['custom']),
	]);

/**
 * Maps page-level remix types to the forcedContentType value expected by the
 * backend.
 */
const PAGE_REMIX_FORCED_CONTENT_TYPE: Record<string, string> = {
	slides: 'slide',
	whiteboard: 'whiteboard',
	database: 'database',
};

type RemixAifcHandoffExperienceAttributes = {
	featureArea: string;
	entryPoint: InteractionSource;
	remixType: RemixOptionSelectionParameters['type'];
	remixSubtype?: string;
	aifcObjectType?: string;
	conversationChannelId?: string;
};

const getRemixAifcHandoffExperienceAttributes = ({
	type,
	subType,
	interactionSource,
	forcedContentType,
}: {
	type: RemixOptionSelectionParameters['type'];
	subType: string | undefined;
	interactionSource: InteractionSource;
	forcedContentType: string | undefined;
}): RemixAifcHandoffExperienceAttributes => ({
	featureArea:
		getRemixFeatureArea({
			remixType: type,
			existingFeatureArea: 'remix',
		}) ?? 'remix',
	entryPoint: interactionSource,
	remixType: type,
	...(subType && { remixSubtype: subType }),
	...(forcedContentType && { aifcObjectType: forcedContentType }),
});

export type PageRemixModalProps = {
	adfDocument?: DocNode | Promise<DocNode>;
	loadAdfDocument?: () => Promise<DocNode | null>;
	currentContentId?: string;
	interactionSource?: InteractionSource;
	onClose: (options?: PageRemixCloseOptions) => void;
	onStopThinking: (options?: { reopenModal?: boolean }) => void;
	isEphemeralViewVisible: boolean;
	/** Render function for the trigger element that the popup anchors to. */
	trigger?: (triggerProps: TriggerProps) => React.ReactNode;
	/** Popup placement relative to the trigger. Defaults to 'left'. */
	popupPlacement?: 'left-end';
	/** Popup offset from the trigger in [along, away] px. */
	popupOffset?: [number, number];
	/** Whether the popup is open. */
	isPopupOpen?: boolean;
	/**
	 * Another Remix flow is active, so picking an option must not start one. Closing the picker
	 * stays available. Admission still rejects any start that gets past this.
	 */
	isRemixStartBlocked?: boolean;
	/** When this value changes the popup will reposition itself. */
	popupRepositionKey?: number;
	/** Show the page-level title and subtitle above the first cascading-menu column. */
	showCascadingHeader?: boolean;
	presentation?: 'popup' | 'modal';
	/**
	 * Called when the sidebar should open with a specific conversation.
	 * Must be invoked from outside the ConfluenceConversationAssistant
	 * wrapper so the chat-open event reaches the sidebar's store.
	 */
	onOpenSidebarConversation?: (conversationId: string) => void;
	prefetchedAttachmentHiddenOptions?: readonly RemixHiddenOptionKind[];
	/** Space-backed options resolved by an owner that mounts before the modal opens. */
	prefetchedSpaceHiddenOptions?: readonly RemixHiddenOptionKind[];
	onAdmitRemixLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	onAdmitRetainedRemixLifecycle?: (
		request: PageRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	onObserveLegacyPhase?: (
		phase: ObjectSidebarRemixPhase,
		entryPath?: PageRemixStartEntryPath,
		token?: symbol,
	) => void;
	isRemixLifecycleTreatmentEnabled?: boolean;
	shouldWaitForRemixHandoffBeforeClose?: boolean;
};

export type PageRemixCloseOptions = {
	remixLifecycleEndReason?: EditorRemixLifecycleEndReason;
	/** Local attribution passed to the lifecycle owner; never used in product analytics. */
	observationToken?: symbol;
	/**
	 * Hide the popup for an intentional transfer (staging preview) without releasing the retained
	 * lifecycle. The caller must close again later with a terminal reason or `remixLifecycleAlreadySettled`.
	 */
	preserveRemixLifecycle?: boolean;
	/**
	 * The lifecycle already ended: the machine reached its own terminal (an accepted INSERTED tears
	 * down with reason `inserted`, and the owner released on that transition), or the owner released
	 * the flow outside this modal. Drop the session's token without calling `end`.
	 */
	remixLifecycleAlreadySettled?: boolean;
};

export type PageRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

export type PageRemixLifecycleAdmissionOutcome =
	| {
			status: 'retained';
			conversationId: string;
			lifecycle: RetainedObjectSidebarRemixLifecycle;
			/** Local attribution passed to the lifecycle owner; never used in product analytics. */
			observationToken?: symbol;
			remixId: string;
			/** Sends a machine event through the owner's token-bound handle. True when the machine accepted it. */
			transition: (event: EditorRemixLifecycleEvent) => boolean;
			/** False once the owner released this lifecycle or moved to another session. */
			isCurrent: () => boolean;
	  }
	| { status: 'legacy' }
	| { status: 'rejected' };

type PageRemixModalImplementationProps = PageRemixModalProps & {
	isLifecycleTreatmentEnabled: boolean;
	machineLifecycle?: {
		remixId?: string;
		conversationId?: string;
		observationToken?: symbol;
		phase: ObjectSidebarRemixLifecycleRead['phase'];
		terminalPhase?: ObjectSidebarRemixLifecycleRead['terminalPhase'];
		isMismatched?: boolean;
		/** The mismatch is the owner having released a flow this modal read before. */
		isReleasedByOwner?: boolean;
	};
	onPageRemixStartRejected?: (
		reason: ObjectSidebarPageRemixStartRejectionReason,
		entryPath: PageRemixStartEntryPath,
	) => void;
	onRemixHandoffIdentityMismatch?: (entryPath: PageRemixStartEntryPath) => void;
};

const PageRemixLifecycleNavigationGuard = ({
	cancelActiveRemixRequest,
	currentContentId,
	onClose,
	originContentId,
	stopThinking,
}: {
	cancelActiveRemixRequest: () => void;
	currentContentId: string | undefined;
	onClose: (options?: PageRemixCloseOptions) => void;
	originContentId: string | undefined;
	stopThinking: () => void;
}): null => {
	useEffect(() => {
		if (!originContentId || !currentContentId || originContentId === currentContentId) {
			return;
		}

		cancelActiveRemixRequest();
		stopThinking();
		onClose({ remixLifecycleEndReason: 'cancelled' });
	}, [cancelActiveRemixRequest, currentContentId, onClose, originContentId, stopThinking]);

	return null;
};

const PageRemixModalImplementation = ({
	adfDocument,
	loadAdfDocument,
	currentContentId,
	interactionSource = 'remixModalFromObjectLevelSidebar',
	onClose: closeModal,
	onStopThinking,
	isEphemeralViewVisible,
	trigger,
	popupPlacement,
	popupOffset,
	isPopupOpen,
	isRemixStartBlocked = false,
	popupRepositionKey,
	showCascadingHeader = false,
	presentation = 'popup',
	onOpenSidebarConversation,
	prefetchedAttachmentHiddenOptions,
	prefetchedSpaceHiddenOptions,
	onAdmitRemixLifecycle,
	onAdmitRetainedRemixLifecycle,
	onObserveLegacyPhase,
	isRemixLifecycleTreatmentEnabled,
	shouldWaitForRemixHandoffBeforeClose = false,
	machineLifecycle,
	isLifecycleTreatmentEnabled,
	onPageRemixStartRejected,
	onRemixHandoffIdentityMismatch,
}: PageRemixModalImplementationProps): React.JSX.Element => {
	const observationTokenRef = useRef<symbol | undefined>();
	const abandonedLifecycleObservationTokensRef = useRef<Set<symbol>>(new Set());
	const lastMachineReadyRemixIdRef = useRef<string | undefined>();
	const handledLifecycleMismatchRemixIdRef = useRef<string | undefined>();
	const onClose = useCallback(
		(options?: PageRemixCloseOptions) => {
			const reason = options?.remixLifecycleEndReason;
			const token =
				options && Object.prototype.hasOwnProperty.call(options, 'observationToken')
					? options.observationToken
					: observationTokenRef.current;
			if (token === undefined || !abandonedLifecycleObservationTokensRef.current.has(token)) {
				if (reason === 'handed-off') {
					onObserveLegacyPhase?.('handed-off', undefined, token);
				} else if (reason === 'error') {
					onObserveLegacyPhase?.('error', undefined, token);
				} else if (reason === 'aborted' || reason === 'cancelled') {
					onObserveLegacyPhase?.('aborted', undefined, token);
				} else if (options?.remixLifecycleAlreadySettled) {
					// The staging transfer settled on an accepted INSERTED (CCCEMAU-3624).
					onObserveLegacyPhase?.('inserted', undefined, token);
				}
			}
			// Forward every close option: the staging transfer relies on `preserveRemixLifecycle` and
			// `remixLifecycleAlreadySettled` reaching the lifecycle owner unchanged.
			closeModal(options);
		},
		[closeModal, onObserveLegacyPhase],
	);
	const [pageContentId] = usePageContentId();
	const resolvedContentId = currentContentId ?? pageContentId;
	const [currentContentType] = useContentType();
	const remixDynamicUiContentType = resolveRemixDynamicUiContentType(currentContentType);
	const isRemixCancelled = useRef(false);
	const hasCommittedRemixCreateTerminal = useRef(false);
	const isDirectOpen = useRef(false);
	// A Rovo-sidebar submission reuses its admitted conversation, so the send does not settle the
	// flow. The result lands in Rovo Chat, and the machine reaching `ready` is what settles it.
	const isReusedRovoConversationRef = useRef(false);
	const activeAdfRequestId = useRef(0);
	const pendingCustomSidebarConversationIdRef = useRef<string | undefined>(undefined);
	const generationConversationChannelIdRef = useRef<string | undefined>(undefined);
	const remixOriginContentIdRef = useRef<string | undefined>(undefined);
	const resolvedContentIdRef = useRef(resolvedContentId);
	resolvedContentIdRef.current = resolvedContentId;
	const pendingResolvedAdfRef = useRef<DocNode | undefined>(undefined);
	const [isThinking, setIsThinking] = useState(false);
	const openAIFCStagingModalRef = useRef<() => void>(() => {});
	const remixAifcHandoffExperienceAttributesRef = useRef<
		RemixAifcHandoffExperienceAttributes | undefined
	>();

	const experienceTracker = useAIMateExperienceTracker();
	const {
		trackGenerationFailed,
		trackGenerationStarted,
		trackGenerationStopped,
		trackGenerationSucceeded,
		trackRemixSubmitted,
	} = useRemixGenerationAnalytics();

	const abortRemixAifcHandoffExperience = useCallback(
		(reason: string) => {
			const attributes = remixAifcHandoffExperienceAttributesRef.current;
			if (!attributes) {
				return;
			}

			experienceTracker.abort({
				name: ExperienceName.REMIX_AIFC_HANDOFF,
				reason,
				attributes: { ...attributes, reason },
			});
			remixAifcHandoffExperienceAttributesRef.current = undefined;
		},
		[experienceTracker],
	);

	const cancelActiveRemixRequest = useCallback(
		(reason = 'User cancelled Remix AIFC handoff') => {
			isRemixCancelled.current = true;
			activeAdfRequestId.current += 1;
			abortRemixAifcHandoffExperience(reason);
		},
		[abortRemixAifcHandoffExperience],
	);

	const unavailableSpaceOptions = useShouldHideDiagramRemixOption({
		enabled: prefetchedSpaceHiddenOptions === undefined,
	});
	const unavailableAttachmentOptions = useShouldHideAttachmentRemixOptions({
		enabled: prefetchedAttachmentHiddenOptions === undefined,
		contentId: resolvedContentId,
	});
	const attachmentHiddenOptions = prefetchedAttachmentHiddenOptions ?? unavailableAttachmentOptions;
	const spaceHiddenOptions = prefetchedSpaceHiddenOptions ?? unavailableSpaceOptions;
	const hiddenOptions =
		spaceHiddenOptions.length > 0
			? [...attachmentHiddenOptions, ...spaceHiddenOptions]
			: attachmentHiddenOptions;

	/**
	 * Ends the flow. Use this instead of calling `onStopThinking` directly: `isThinking` has to be
	 * cleared on every path that ends the flow, or the thinking bar it drives is left behind.
	 */
	const stopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			setIsThinking(false);
			onStopThinking(options);
		},
		[onStopThinking],
	);

	const hidePopupForTransfer = useCallback(() => {
		stopThinking();
		onClose({ preserveRemixLifecycle: true });
	}, [onClose, stopThinking]);
	const stagingTransfer = useObjectSidebarStagingTransfer({ hidePopup: hidePopupForTransfer });
	const settleStagingTransfer = useCallback(
		(terminal: ObjectSidebarStagingTransferTerminal) => {
			hasCommittedRemixCreateTerminal.current = true;
			stopThinking();
			onClose(
				terminal === 'inserted'
					? { remixLifecycleAlreadySettled: true }
					: { remixLifecycleEndReason: terminal },
			);
		},
		[onClose, stopThinking],
	);
	const beginStagingTransfer = useCallback(
		(lifecycle: ObjectSidebarStagingTransferLifecycle) =>
			stagingTransfer.begin({ lifecycle, settle: settleStagingTransfer }),
		[settleStagingTransfer, stagingTransfer],
	);

	const openModalAndCloseOverlay = useCallback(() => {
		hasCommittedRemixCreateTerminal.current = true;
		const pendingCustomSidebarConversationId = pendingCustomSidebarConversationIdRef.current;
		if (pendingCustomSidebarConversationId) {
			pendingCustomSidebarConversationIdRef.current = undefined;
			onOpenSidebarConversation?.(pendingCustomSidebarConversationId);
			stopThinking();
			onClose({
				remixLifecycleEndReason: 'handed-off',
				...(observationTokenRef.current !== undefined && {
					observationToken: observationTokenRef.current,
				}),
			});
			// The deferred reply has already finished, so nothing is left generating to hold for.
			releaseRemixHandoffHold(pendingCustomSidebarConversationId, 'already-finished');
			return;
		}

		onObserveLegacyPhase?.('previewing', undefined, observationTokenRef.current);
		// Read before closing: ending the flow resets the lifecycle read.
		const previewConversationId = machineLifecycle?.conversationId;
		openAIFCStagingModalRef.current();
		stopThinking();
		onClose({
			remixLifecycleEndReason: 'handed-off',
			...(observationTokenRef.current !== undefined && {
				observationToken: observationTokenRef.current,
			}),
		});
		// The preview already has its result, so nothing is left generating to hold for.
		if (previewConversationId) {
			releaseRemixHandoffHold(previewConversationId, 'already-finished');
		}
	}, [
		machineLifecycle?.conversationId,
		onClose,
		onObserveLegacyPhase,
		onOpenSidebarConversation,
		stopThinking,
	]);

	const {
		showProgressBar,
		isComplete,
		onCompleteAnimationFinished,
		signalActionReady,
		resetCompletion,
	} = useRemixThinkingBarProgress({ onOpenPreview: openModalAndCloseOverlay });

	const handleLifecycleReady = useCallback(() => {
		if (isRemixCancelled.current) {
			return;
		}
		if (isReusedRovoConversationRef.current) {
			// The send left this flow open because Rovo Chat kept driving it. Its result is complete
			// now, so end the flow here or the retained lifecycle stays in `ready` and every Object
			// Sidebar Remix entry point stays disabled (CCCEMAU-3757). There is no preview to open.
			isReusedRovoConversationRef.current = false;
			hasCommittedRemixCreateTerminal.current = true;
			succeedRemixCreateExperienceAtSidebarHandoff(experienceTracker, presentation === 'popup');
			// Read before closing: ending the flow resets the lifecycle read.
			const reusedConversationId = machineLifecycle?.conversationId;
			stopThinking();
			onClose({
				remixLifecycleEndReason: 'handed-off',
				...(observationTokenRef.current !== undefined && {
					observationToken: observationTokenRef.current,
				}),
			});
			// The result is already complete, so nothing is left generating to hold for.
			if (reusedConversationId) {
				releaseRemixHandoffHold(reusedConversationId, 'already-finished');
			}
			return;
		}
		// For types in OPEN_AIFC_MODAL_TYPES, the modal is
		// already open — skip reopening it via onActionReady.
		if (isDirectOpen.current) {
			return;
		}
		remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED);
		experienceTracker.succeed({ name: ExperienceName.REMIX_START });
		void remixGeneratePreviewExperience.success();
		signalActionReady();
	}, [
		signalActionReady,
		experienceTracker,
		presentation,
		machineLifecycle?.conversationId,
		onClose,
		stopThinking,
	]);
	const onActionReady = useCallback(() => {
		if (isRemixCancelled.current) {
			return;
		}
		const conversationChannelId = generationConversationChannelIdRef.current;
		generationConversationChannelIdRef.current = undefined;
		trackGenerationSucceeded(
			conversationChannelId === undefined ? undefined : { conversationChannelId },
		);
		onObserveLegacyPhase?.('ready', undefined, observationTokenRef.current);
		if (!machineLifecycle) {
			handleLifecycleReady();
		}
	}, [handleLifecycleReady, machineLifecycle, onObserveLegacyPhase, trackGenerationSucceeded]);

	const {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError: legacyIsRemixError,
		isRemixAborted: legacyIsRemixAborted,
		openAIFCStagingModal,
		closeAIFCStagingModal,
	} = useRemixToAIFC({
		onActionReady,
		// Staging callbacks are bound per transfer when the modal opens (CCCEMAU-3624).
		stagingAreaTargetMode: 'generating-from-content',
	});
	const isRemixError = machineLifecycle
		? machineLifecycle.terminalPhase === 'error'
		: legacyIsRemixError;
	const isRemixAborted = machineLifecycle
		? machineLifecycle.terminalPhase === 'aborted'
		: legacyIsRemixAborted;
	useEffect(() => {
		const remixId = machineLifecycle?.remixId;
		if (
			machineLifecycle?.phase === 'ready' &&
			remixId !== undefined &&
			lastMachineReadyRemixIdRef.current !== remixId
		) {
			lastMachineReadyRemixIdRef.current = remixId;
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- signals lifecycle readiness in response to the machine phase, not a local state change
			handleLifecycleReady();
		}
	}, [handleLifecycleReady, machineLifecycle]);
	useEffect(() => {
		const remixId = machineLifecycle?.remixId;
		const conversationId = machineLifecycle?.conversationId;
		if (
			!machineLifecycle?.isMismatched ||
			remixId === undefined ||
			handledLifecycleMismatchRemixIdRef.current === remixId
		) {
			return;
		}
		handledLifecycleMismatchRemixIdRef.current = remixId;
		if (machineLifecycle.observationToken !== undefined) {
			abandonedLifecycleObservationTokensRef.current.add(machineLifecycle.observationToken);
		}
		const reason = machineLifecycle.isReleasedByOwner
			? 'lifecycle released by owner'
			: 'lifecycle read mismatch';
		cancelActiveRemixRequest(reason);
		isDirectOpen.current = false;
		isReusedRovoConversationRef.current = false;
		pendingCustomSidebarConversationIdRef.current = undefined;
		generationConversationChannelIdRef.current = undefined;
		if (conversationId) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- stops only the mismatched flow's Rovo generation
			stopRemixing(conversationId);
		}
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- closes a flow whose retained lifecycle cannot be read, not a local state change
		stopThinking();
		if (isEphemeralViewVisible) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- closes staging as part of mismatched lifecycle cleanup
			closeAIFCStagingModal();
		}
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason,
			attributes: { reason },
		});
		abortRemixCreateExperience(experienceTracker, reason);
		void remixGeneratePreviewExperience.abort({ metadata: { reason } });
		onClose({
			...(machineLifecycle.isReleasedByOwner
				? { remixLifecycleAlreadySettled: true }
				: { remixLifecycleEndReason: 'abandoned' }),
			observationToken: observationTokenRef.current,
		});
	}, [
		closeAIFCStagingModal,
		cancelActiveRemixRequest,
		experienceTracker,
		isEphemeralViewVisible,
		machineLifecycle,
		onClose,
		stopThinking,
		stopRemixing,
	]);
	openAIFCStagingModalRef.current = openAIFCStagingModal;

	const experienceTrackerRef = useRef(experienceTracker);
	experienceTrackerRef.current = experienceTracker;
	useEffect(() => {
		return () => {
			activeAdfRequestId.current += 1;
			experienceTrackerRef.current.abort({
				name: ExperienceName.REMIX_START,
				reason: 'User closed remix overlay',
				attributes: { reason: 'User closed remix overlay' },
			});
			abortRemixCreateExperienceIfNotCommitted(
				experienceTrackerRef.current,
				hasCommittedRemixCreateTerminal.current,
			);
			void remixGeneratePreviewExperience.abort({
				metadata: { reason: 'User closed remix overlay' },
			});
			if (!isDirectOpen.current) {
				abortRemixAifcHandoffExperience('User closed remix overlay');
			}
		};
	}, [abortRemixAifcHandoffExperience]);

	const handleClose = useCallback(() => {
		cancelActiveRemixRequest();
		const reason = 'User closed remix overlay';
		experienceTracker.abort({
			name: ExperienceName.REMIX_START,
			reason,
			attributes: { reason },
		});
		abortRemixCreateExperience(experienceTracker, reason);
		onClose({ remixLifecycleEndReason: 'cancelled' });
	}, [cancelActiveRemixRequest, experienceTracker, onClose]);

	const onRemixFlowActiveChange = useCallback(
		(active: boolean, options?: { shouldShowThinkingBar?: boolean }) => {
			if (active) {
				if (isLifecycleTreatmentEnabled || shouldWaitForRemixHandoffBeforeClose) {
					hasCommittedRemixCreateTerminal.current = false;
				}
				isRemixCancelled.current = false;
				isDirectOpen.current = false;
				isReusedRovoConversationRef.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				if (options?.shouldShowThinkingBar ?? true) {
					setIsThinking(true);
				}
				// Clear stale completion state from any previous flow.
				resetCompletion();
			} else {
				if (stagingTransfer.isPending()) {
					// The request feeds the open staging preview; the transfer controller settles it.
					return;
				}
				cancelActiveRemixRequest();
				isDirectOpen.current = false;
				isReusedRovoConversationRef.current = false;
				pendingCustomSidebarConversationIdRef.current = undefined;
				experienceTracker.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User cancelled remix thinking',
					attributes: { reason: 'User cancelled remix thinking' },
				});
				abortRemixCreateExperience(experienceTracker, 'User cancelled remix thinking');
				void remixGeneratePreviewExperience.abort({
					metadata: { reason: 'User cancelled remix thinking' },
				});
				trackGenerationStopped();
				stopRemixing();
				const restartAttributes = {
					entryPoint: PAGE_REMIX_ENTRY_POINT,
					remixSource: 'cancelledRemixThinking',
					originalRemixSource: PAGE_REMIX_SOURCE,
					product: 'confluence',
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: restartAttributes,
				});
				startRemixCreateExperience({ experienceTracker, attributes: restartAttributes });
				stopThinking(shouldWaitForRemixHandoffBeforeClose ? undefined : { reopenModal: true });
				if (shouldWaitForRemixHandoffBeforeClose) {
					onClose({ remixLifecycleEndReason: 'cancelled' });
				}
			}
		},
		[
			stopRemixing,
			stopThinking,
			experienceTracker,
			resetCompletion,
			cancelActiveRemixRequest,
			trackGenerationStopped,
			onClose,
			shouldWaitForRemixHandoffBeforeClose,
			isLifecycleTreatmentEnabled,
			stagingTransfer,
		],
	);

	const prevIsRemixError = useRef(isRemixError);
	useEffect(() => {
		if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
			experienceTracker.fail({
				teamName: 'CC CE MAU',
				name: ExperienceName.REMIX_START,
				error: new Error('Rovo agent error during remix'),
			});
			void remixGeneratePreviewExperience.failure({
				metadata: { reason: 'Rovo agent error during remix' },
			});
			trackGenerationFailed();
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- ends the flow in response to Rovo reporting an error, not to a local state change
			stopThinking({ reopenModal: true });
		}
		prevIsRemixError.current = isRemixError;
	}, [isRemixError, stopThinking, experienceTracker, trackGenerationFailed]);

	useEffect(() => {
		if (
			!isRemixCancelled.current &&
			isRemixError &&
			(!shouldWaitForRemixHandoffBeforeClose || !hasCommittedRemixCreateTerminal.current)
		) {
			if (shouldWaitForRemixHandoffBeforeClose) {
				hasCommittedRemixCreateTerminal.current = true;
			}
			if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to Rovo reporting an error, not to a local state change
				closeAIFCStagingModal();
			}
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to Rovo reporting an error, not to a local state change
			stopThinking();
			onClose({
				remixLifecycleEndReason: 'error',
				...(isLifecycleTreatmentEnabled && machineLifecycle?.observationToken !== undefined
					? { observationToken: machineLifecycle.observationToken }
					: {}),
			});
		}
	}, [
		isRemixError,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		stopThinking,
		onClose,
		shouldWaitForRemixHandoffBeforeClose,
		isLifecycleTreatmentEnabled,
		machineLifecycle?.observationToken,
	]);

	// Dismiss the thinking bar and close the popup when the user stops the remix
	// from the Rovo side chat.
	useEffect(() => {
		if (
			isRemixAborted &&
			!isRemixCancelled.current &&
			(!shouldWaitForRemixHandoffBeforeClose || !hasCommittedRemixCreateTerminal.current)
		) {
			if (shouldWaitForRemixHandoffBeforeClose) {
				hasCommittedRemixCreateTerminal.current = true;
			}
			const reason = 'User stopped from Rovo side chat';
			experienceTracker.abort({
				name: ExperienceName.REMIX_START,
				reason,
				attributes: { reason },
			});
			abortRemixCreateExperience(experienceTracker, reason);
			void remixGeneratePreviewExperience.abort({
				metadata: { reason },
			});
			trackGenerationStopped();
			if (isEphemeralViewVisible) {
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to the user stopping the remix in Rovo, not to a local state change
				closeAIFCStagingModal();
			}
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- tears down the flow in response to the user stopping the remix in Rovo, not to a local state change
			stopThinking();
			onClose({
				remixLifecycleEndReason:
					isLifecycleTreatmentEnabled && shouldWaitForRemixHandoffBeforeClose
						? 'cancelled'
						: 'aborted',
				...(isLifecycleTreatmentEnabled && machineLifecycle?.observationToken !== undefined
					? { observationToken: machineLifecycle.observationToken }
					: {}),
			});
		}
	}, [
		isRemixAborted,
		isEphemeralViewVisible,
		closeAIFCStagingModal,
		stopThinking,
		onClose,
		experienceTracker,
		trackGenerationStopped,
		isLifecycleTreatmentEnabled,
		shouldWaitForRemixHandoffBeforeClose,
		machineLifecycle?.observationToken,
	]);

	const handleOptionSelect = useCallback(
		async (
			{
				type,
				subType,
				userPrompt,
				conversationId,
			}: RemixOptionSelectionParameters & { conversationId?: string },
			options?: {
				isMachineBackedStart?: boolean;
				isRovoChatSubmission?: boolean;
				requestToken?: symbol;
				retainedLifecycle?: ObjectSidebarStagingTransferLifecycle;
			},
		) => {
			const isLifecycleTreatmentStart = options !== undefined;
			const isMachineBackedStart = options?.isMachineBackedStart ?? false;
			const requestToken = options?.requestToken;
			const isRovoChatSubmission = isLifecycleTreatmentStart
				? (options?.isRovoChatSubmission ?? false)
				: conversationId !== undefined &&
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');
			/**
			 * Confirms Rovo accepted the work before this flow can be treated as handed off.
			 *
			 * A machine-backed start is bound to one authoritative conversation identity, so the
			 * send has to come back with that exact identity. An empty or different identity is a
			 * failed handoff, not a quiet substitution: publish the failure, record it, and throw
			 * so the caller settles through the common truthful-failure path.
			 */
			const assertHandoffAcknowledged = (resolvedConversationId: string | undefined): void => {
				if (!isMachineBackedStart || !conversationId) {
					return;
				}
				if (resolvedConversationId && resolvedConversationId === conversationId) {
					return;
				}
				onRemixHandoffIdentityMismatch?.(isRovoChatSubmission ? 'rovo-sidebar' : 'popup-option');
				publishPageRemixHandoffFailed();
				throw new Error(
					'Rovo did not acknowledge the Remix handoff for the requested conversation',
				);
			};
			const isCustomOnlyPrompt = type === 'custom' && !subType && !!userPrompt?.trim();
			const shouldDeferCustomOnlyPrompt =
				isCustomOnlyPrompt && expValEquals('cc_maui_polish_changes_batch_5', 'isEnabled', true);
			const adfRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = adfRequestId;
			const shouldReuseRovoConversation = isLifecycleTreatmentStart
				? isRovoChatSubmission && Boolean(conversationId)
				: Boolean(conversationId);
			const emitsStartSuccessBeforeAwait =
				isLifecycleTreatmentStart && !shouldReuseRovoConversation && !shouldDeferCustomOnlyPrompt;

			experienceTracker.updateAttributes({
				name: ExperienceName.REMIX_START,
				attributes: {
					remixType: type,
					remixSubtype: subType,
					includesFreegenPrompt: !!userPrompt,
					...(conversationId && { conversationChannelId: conversationId }),
				},
			});
			const forcedContentType = PAGE_REMIX_FORCED_CONTENT_TYPE[type];
			const isRemixAifcHandoff = OPEN_AIFC_MODAL_TYPES.has(type);
			const remixAifcHandoffExperienceAttributes = isRemixAifcHandoff
				? getRemixAifcHandoffExperienceAttributes({
						type,
						subType,
						interactionSource,
						forcedContentType,
					})
				: undefined;

			if (remixAifcHandoffExperienceAttributes) {
				remixAifcHandoffExperienceAttributesRef.current = remixAifcHandoffExperienceAttributes;
				experienceTracker.start({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					id: ExperienceName.REMIX_AIFC_HANDOFF,
					attributes: remixAifcHandoffExperienceAttributes,
				});
			}

			void remixGeneratePreviewExperience.start();
			remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
			remixGeneratePreviewExperience.addMetadata({
				product: 'confluence',
				interactionSource,
				remixType: type,
				remixSubtype: subType,
				entryPoint: PAGE_REMIX_ENTRY_POINT,
				contentType: remixDynamicUiContentType,
				includesFreegenPrompt: !!userPrompt,
				...(type === 'infographic'
					? {
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
						}
					: {}),
			});
			const generationAnalyticsAttributes = {
				contentId: resolvedContentId,
				...(conversationId && { conversationChannelId: conversationId }),
				entryPoint: PAGE_REMIX_ENTRY_POINT,
				includesFreegenPrompt: !!userPrompt,
				product: 'confluence',
				remixSource: interactionSource,
				remixSubtype: subType,
				remixType: type,
			};
			trackRemixSubmitted(generationAnalyticsAttributes);
			trackGenerationStarted(generationAnalyticsAttributes);
			generationConversationChannelIdRef.current = conversationId;

			let resolvedAdfDocument: DocNode;
			try {
				if (pendingResolvedAdfRef.current) {
					resolvedAdfDocument = pendingResolvedAdfRef.current;
					pendingResolvedAdfRef.current = undefined;
				} else {
					const sourceAdf = adfDocument ?? EMPTY_PAGE_ADF;
					resolvedAdfDocument = sourceAdf instanceof Promise ? await sourceAdf : sourceAdf;
				}
			} catch (error) {
				if (activeAdfRequestId.current !== adfRequestId) {
					return;
				}

				if (
					UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2')
				) {
					publishPageRemixHandoffFailed();
				}

				experienceTracker.fail({
					teamName: 'CC CE MAU',
					name: ExperienceName.REMIX_START,
					error: error instanceof Error ? error : new Error('Failed to load content for Remix'),
				});
				void remixGeneratePreviewExperience.failure({
					metadata: { reason: 'Failed to load content for Remix' },
				});
				trackGenerationFailed();
				if (remixAifcHandoffExperienceAttributes) {
					experienceTracker.fail({
						teamName: 'CC CE MAU',
						name: ExperienceName.REMIX_AIFC_HANDOFF,
						error:
							error instanceof Error ? error : new Error('Failed to prepare Remix AIFC handoff'),
						attributes: remixAifcHandoffExperienceAttributes,
					});
					remixAifcHandoffExperienceAttributesRef.current = undefined;
				}
				stopThinking();
				onClose({ remixLifecycleEndReason: 'cancelled', observationToken: requestToken });
				return;
			}

			if (activeAdfRequestId.current !== adfRequestId) {
				return;
			}

			const dynamicUiSource = constructPageDynamicUiSource(
				resolvedAdfDocument,
				remixDynamicUiContentType,
			);
			const additionalCreationContextParams = getPageRemixObjectCreationContext(type);
			const promptText = isRovoChatSubmission
				? userPrompt
				: type === 'slides'
					? `Remix the selected content as ${getTypeDisplayName(type)}`
					: undefined;

			const promptArgs = {
				userPrompt: isRovoChatSubmission ? undefined : userPrompt,
				promptText,
				dynamicUiType: type,
				dynamicUiSubtype: subType,
				dynamicUiSource,
				interactionSource,
				additionalCreationContextParams,
				...(conversationId ? { conversationId } : {}),
			};

			if (!OPEN_AIFC_MODAL_TYPES.has(type)) {
				isDirectOpen.current = !shouldDeferCustomOnlyPrompt;
				// A deferred custom-only prompt still settles through the preview on `ready`.
				isReusedRovoConversationRef.current =
					isLifecycleTreatmentStart && shouldReuseRovoConversation && !shouldDeferCustomOnlyPrompt;
				// A machine-backed start has to await the prompt even before the retained
				// lifecycle has propagated into `shouldWaitForRemixHandoffBeforeClose`, so a
				// native-send rejection can be rethrown and settle the lifecycle as cancelled.
				const shouldWaitForPromptBeforeClose =
					shouldReuseRovoConversation ||
					shouldWaitForRemixHandoffBeforeClose ||
					isMachineBackedStart;
				// Both the legacy no-wait path and the treatment's early-success path settle
				// REMIX_START before the send resolves, so capture the option before either succeeds.
				const tracksOptionBeforePromptResolution =
					emitsStartSuccessBeforeAwait || !shouldWaitForPromptBeforeClose;
				if (tracksOptionBeforePromptResolution) {
					trackRemixCreateOptionSelected({
						experienceTracker,
						conversationId: undefined,
						remixType: type,
						remixSubtype: subType,
						includesFreegenPrompt: !!userPrompt,
					});
				}
				const promptPromise = sendAIFCPrompt(promptArgs).then((conversationChannelId) => {
					generationConversationChannelIdRef.current = conversationChannelId;
					if (conversationChannelId && !isRemixCancelled.current && !shouldReuseRovoConversation) {
						assertHandoffAcknowledged(conversationChannelId);
						if (shouldDeferCustomOnlyPrompt) {
							pendingCustomSidebarConversationIdRef.current = conversationChannelId;
						} else {
							onOpenSidebarConversation?.(conversationChannelId);
						}
					}
					if (!tracksOptionBeforePromptResolution) {
						trackRemixCreateOptionSelected({
							experienceTracker,
							conversationId: conversationChannelId,
							remixType: type,
							remixSubtype: subType,
							includesFreegenPrompt: !!userPrompt,
						});
					}
					if (
						conversationChannelId &&
						!isRemixCancelled.current &&
						!shouldReuseRovoConversation &&
						!shouldDeferCustomOnlyPrompt
					) {
						succeedRemixCreateExperienceAtSidebarHandoff(
							experienceTracker,
							presentation === 'popup',
						);
					}
					return conversationChannelId;
				});
				if (emitsStartSuccessBeforeAwait) {
					experienceTracker.succeed({ name: ExperienceName.REMIX_START });
					void remixGeneratePreviewExperience.success();
				}

				let conversationChannelId: string | undefined;
				if (shouldWaitForPromptBeforeClose) {
					try {
						conversationChannelId = await promptPromise;
					} catch (error) {
						const isLifecycleAbandoned =
							isLifecycleTreatmentStart &&
							requestToken !== undefined &&
							abandonedLifecycleObservationTokensRef.current.has(requestToken);
						if (!isLifecycleAbandoned) {
							if (
								UNSAFE_expValEqualsNoExposure(
									'cc-ce-maui-remix-menu-multivariant',
									'cohort',
									'variant2',
								)
							) {
								publishPageRemixHandoffFailed();
							}
							if (!isLifecycleTreatmentStart || shouldReuseRovoConversation) {
								trackGenerationFailed();
							}
						}
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return;
					}
				} else {
					void promptPromise.catch(() => {
						if (
							UNSAFE_expValEqualsNoExposure(
								'cc-ce-maui-remix-menu-multivariant',
								'cohort',
								'variant2',
							)
						) {
							publishPageRemixHandoffFailed();
						}
					});
				}

				if (shouldWaitForPromptBeforeClose) {
					assertHandoffAcknowledged(conversationChannelId);
				}

				if (!shouldDeferCustomOnlyPrompt) {
					if (shouldWaitForPromptBeforeClose && !conversationChannelId) {
						return;
					}
					if (!emitsStartSuccessBeforeAwait) {
						experienceTracker.succeed({ name: ExperienceName.REMIX_START });
						void remixGeneratePreviewExperience.success();
					}
					if (!shouldReuseRovoConversation) {
						hasCommittedRemixCreateTerminal.current = true;
						// The popup-mode RemixContainer stays mounted after close and shows its thinking bar
						// whenever thinking is on, so end thinking here or the bar outlives the handoff.
						stopThinking();
						onClose({ remixLifecycleEndReason: 'handed-off', observationToken: requestToken });
					}
				}
			} else {
				isDirectOpen.current = true;
				const promptPromise = sendAIFCPrompt({
					...promptArgs,
					forcedContentType,
				})
					.then((conversationChannelId) => {
						generationConversationChannelIdRef.current = conversationChannelId;
						trackRemixCreateOptionSelected({
							experienceTracker,
							conversationId: conversationChannelId,
							remixType: type,
							remixSubtype: subType,
							includesFreegenPrompt: !!userPrompt,
						});
						if (!isRemixCancelled.current) {
							const attributes = {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationChannelId }),
							};
							experienceTracker.succeed({
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								attributes,
							});
							succeedRemixCreateExperienceAtHandoff(experienceTracker, {
								...remixAifcHandoffExperienceAttributes,
								...(conversationChannelId && { conversationId: conversationChannelId }),
							});
							remixAifcHandoffExperienceAttributesRef.current = undefined;
							experienceTracker.succeed({ name: ExperienceName.REMIX_START });
							void remixGeneratePreviewExperience.success();
							// A machine-backed start is bound to one conversation identity. The awaiting caller
							// settles a mismatch as a failed handoff, so neither the staging preview nor a
							// transfer may begin for a flow that is about to be cancelled.
							const isHandoffAcknowledged =
								!isMachineBackedStart ||
								!conversationId ||
								conversationChannelId === conversationId;
							if (isHandoffAcknowledged) {
								// Staging types keep ownership until the destination acknowledges (CCCEMAU-3624).
								// The transfer begins before the modal opens so the store's open callback can
								// never race ahead, and the callbacks handed to this opening are bound to this
								// transfer alone: a late callback from an earlier modal, or the editor's deferred
								// insertion acknowledgement, cannot settle a later flow. If openAIFCStagingModal
								// bails on a cancelled conversation, the abort effect settles the pending transfer.
								const transferCallbacks = options?.retainedLifecycle
									? stagingTransfer.callbacksFor(beginStagingTransfer(options.retainedLifecycle))
									: undefined;
								openAIFCStagingModal({ forceFullModal: true, ...transferCallbacks });
							}
						}
						return conversationChannelId;
					})
					.catch((error) => {
						if (!isRemixCancelled.current) {
							if (
								UNSAFE_expValEqualsNoExposure(
									'cc-ce-maui-remix-menu-multivariant',
									'cohort',
									'variant2',
								)
							) {
								publishPageRemixHandoffFailed();
							}
							const handoffError =
								error instanceof Error ? error : new Error('Failed to complete Remix AIFC handoff');

							experienceTracker.fail({
								teamName: 'CC CE MAU',
								name: ExperienceName.REMIX_AIFC_HANDOFF,
								error: handoffError,
								attributes: remixAifcHandoffExperienceAttributes,
							});
							experienceTracker.fail({
								teamName: 'CC CE MAU',
								name: ExperienceName.REMIX_START,
								error: handoffError,
							});
							void remixGeneratePreviewExperience.failure({
								metadata: { reason: 'Failed to complete Remix AIFC handoff' },
							});
							trackGenerationFailed();
							remixAifcHandoffExperienceAttributesRef.current = undefined;
						}
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return undefined;
					});
				const shouldWaitForPromptBeforeClose =
					shouldReuseRovoConversation ||
					shouldWaitForRemixHandoffBeforeClose ||
					isMachineBackedStart;
				if (shouldWaitForPromptBeforeClose) {
					let conversationChannelId: string | undefined;
					try {
						conversationChannelId = await promptPromise;
					} catch (error) {
						if (shouldReuseRovoConversation || isMachineBackedStart) {
							throw error;
						}
						return;
					}
					assertHandoffAcknowledged(conversationChannelId);
					if (!conversationChannelId) {
						return;
					}
				}
				if (options?.retainedLifecycle) {
					return; // settled later by the staging transfer controller
				}
				hasCommittedRemixCreateTerminal.current = true;
				// The popup-mode RemixContainer stays mounted after close and shows its thinking bar
				// whenever thinking is on, so end thinking here or the bar outlives the handoff.
				stopThinking();
				onClose({ remixLifecycleEndReason: 'handed-off', observationToken: requestToken });
			}
		},
		[
			beginStagingTransfer,
			stagingTransfer,
			sendAIFCPrompt,
			adfDocument,
			interactionSource,
			presentation,
			openAIFCStagingModal,
			onClose,
			stopThinking,
			experienceTracker,
			onOpenSidebarConversation,
			onRemixHandoffIdentityMismatch,
			remixDynamicUiContentType,
			resolvedContentId,
			shouldWaitForRemixHandoffBeforeClose,
			trackGenerationFailed,
			trackGenerationStarted,
			trackRemixSubmitted,
		],
	);

	const preparePageRemixRequest = useCallback(
		async (
			params: PageRemixRequestParameters,
			entryPath: PageRemixStartEntryPath,
			submitContext?: PageRemixSubmitContext,
		): Promise<PreparedPageRemixRequest> => {
			const startRequestId = activeAdfRequestId.current + 1;
			activeAdfRequestId.current = startRequestId;
			const originContentId = resolvedContentId;
			const admission: { outcome: PageRemixLifecycleAdmissionOutcome } = {
				outcome: { status: 'legacy' },
			};
			const startResult = await prepareObjectSidebarPageRemixStart({
				resolveAdfDocument: async () => {
					if (loadAdfDocument) {
						return loadAdfDocument();
					}
					if (adfDocument instanceof Promise) {
						return adfDocument;
					}
					return adfDocument ?? null;
				},
				admitLifecycle: onAdmitRetainedRemixLifecycle
					? (request) => {
							const lifecycleAdmission = onAdmitRetainedRemixLifecycle(request);
							admission.outcome = lifecycleAdmission;
							if (lifecycleAdmission.status === 'retained') {
								observationTokenRef.current = lifecycleAdmission.observationToken;
								onObserveLegacyPhase?.('thinking', entryPath, lifecycleAdmission.observationToken);
							}
							return lifecycleAdmission.status !== 'rejected';
						}
					: undefined,
				conversationId: params.conversationId,
				isCurrent: () =>
					activeAdfRequestId.current === startRequestId &&
					resolvedContentIdRef.current === originContentId,
				...(submitContext && { isOpenActionCurrent: submitContext.isCurrentOpenAction }),
				onRejected: (reason) => onPageRemixStartRejected?.(reason, entryPath),
			});

			if (startResult.status === 'rejected') {
				return startResult;
			}

			const lifecycleAdmission = admission.outcome;
			pendingResolvedAdfRef.current = startResult.adfDocument;
			if (lifecycleAdmission.status === 'retained') {
				remixOriginContentIdRef.current = originContentId;
				return {
					status: 'started',
					lifecycleAdmission,
					params: {
						...params,
						conversationId: lifecycleAdmission.conversationId,
					},
				};
			}

			return { status: 'started', lifecycleAdmission, params };
		},
		[
			adfDocument,
			loadAdfDocument,
			onAdmitRetainedRemixLifecycle,
			onObserveLegacyPhase,
			onPageRemixStartRejected,
			resolvedContentId,
		],
	);

	const handleObjectSidebarOptionSelect = useCallback(
		async (params: RemixOptionSelectionParameters) => {
			const preparedRequest = await preparePageRemixRequest(params, 'popup-option');
			if (preparedRequest.status === 'rejected') {
				stopThinking({ reopenModal: true });
				return;
			}

			try {
				await runObjectSidebarPageRemixNativeStart({
					startNativeRemix: () =>
						handleOptionSelect(preparedRequest.params, {
							isMachineBackedStart: preparedRequest.lifecycleAdmission.status === 'retained',
							isRovoChatSubmission: false,
							requestToken:
								preparedRequest.lifecycleAdmission.status === 'retained'
									? preparedRequest.lifecycleAdmission.observationToken
									: undefined,
							retainedLifecycle:
								preparedRequest.lifecycleAdmission.status === 'retained'
									? preparedRequest.lifecycleAdmission
									: undefined,
						}),
					onNativeStartRejected: () => {
						stopThinking({ reopenModal: true });
						onClose({
							remixLifecycleEndReason: 'cancelled',
							observationToken:
								preparedRequest.lifecycleAdmission.status === 'retained'
									? preparedRequest.lifecycleAdmission.observationToken
									: undefined,
						});
					},
				});
			} catch {
				// The native-start wrapper has already restored the picker and settled the lifecycle.
			}
		},
		[handleOptionSelect, onClose, preparePageRemixRequest, stopThinking],
	);

	// The picker turns thinking on through `onRemixFlowActiveChange` without the picked type, so
	// `skipThinkingTypes` only covers its uncontrolled state. Once `isThinking` is controlled, record
	// whether the pick skips the bar so a hand-off type never flashes it before the hand-off.
	const isPickerThinkingControlled =
		isLifecycleTreatmentEnabled || shouldWaitForRemixHandoffBeforeClose;
	const [isPickerThinkingBarSkipped, setIsPickerThinkingBarSkipped] = useState(false);
	const pickerOptionSelectHandler = isLifecycleTreatmentEnabled
		? handleObjectSidebarOptionSelect
		: handleOptionSelect;
	const handlePickerOptionSelect = useCallback(
		(params: RemixOptionSelectionParameters) => {
			if (isPickerThinkingControlled) {
				setIsPickerThinkingBarSkipped(getPageRemixSkipThinkingTypes().has(params.type));
			}
			return pickerOptionSelectHandler(params);
		},
		[isPickerThinkingControlled, pickerOptionSelectHandler],
	);

	const disarmIdlePageRemix = useCallback(() => {
		pendingResolvedAdfRef.current = undefined;
	}, []);

	const handleVariantTwoPageRemixRequest = useCallback(
		async (params: PageRemixRequestParameters, submitContext: PageRemixSubmitContext) => {
			if (!isLifecycleTreatmentEnabled) {
				let resolvedAdf: DocNode | null = null;
				try {
					if (loadAdfDocument) {
						resolvedAdf = await loadAdfDocument();
					} else if (adfDocument instanceof Promise) {
						resolvedAdf = await adfDocument;
					} else {
						resolvedAdf = adfDocument ?? null;
					}
				} catch {
					publishPageRemixHandoffFailed();
					throw new Error('Failed to load page ADF for Remix');
				}
				if (!resolvedAdf || isEmptyPageAdf(resolvedAdf)) {
					publishPageRemixHandoffFailed();
					throw new Error('Page content is empty');
				}
				if (
					onAdmitRemixLifecycle &&
					!onAdmitRemixLifecycle({ conversationId: params.conversationId })
				) {
					publishPageRemixHandoffFailed();
					throw new Error('Failed to admit page Remix lifecycle');
				}

				pendingResolvedAdfRef.current = resolvedAdf;
				onRemixFlowActiveChange(true, {
					shouldShowThinkingBar: !getPageRemixSkipThinkingTypes().has(params.type),
				});
				try {
					await handleOptionSelect(params);
				} catch {
					stopThinking({ reopenModal: true });
					onClose({ remixLifecycleEndReason: 'error' });
					throw new Error('Failed to start page remix from Rovo submit');
				}
				return;
			}

			const startResult = await preparePageRemixRequest(params, 'rovo-sidebar', submitContext);

			if (startResult.status === 'rejected') {
				publishPageRemixHandoffFailed();
				if (startResult.reason === 'adf-load-failed') {
					throw new Error('Failed to load page ADF for Remix');
				}
				if (startResult.reason === 'empty-adf') {
					throw new Error('Page content is empty');
				}
				if (startResult.reason === 'malformed-adf') {
					throw new Error('Page content is malformed');
				}
				if (startResult.reason === 'stale-adf') {
					throw new Error('Page content changed while preparing Remix');
				}
				if (startResult.reason === 'stale-open-action') {
					throw new Error('Remix request no longer matches the open Remix action');
				}
				throw new Error('Failed to admit page Remix lifecycle');
			}

			onRemixFlowActiveChange(true, {
				shouldShowThinkingBar: !getPageRemixSkipThinkingTypes().has(params.type),
			});
			await runObjectSidebarPageRemixNativeStart({
				startNativeRemix: () =>
					handleOptionSelect(startResult.params, {
						isMachineBackedStart: startResult.lifecycleAdmission.status === 'retained',
						isRovoChatSubmission: true,
						requestToken:
							startResult.lifecycleAdmission.status === 'retained'
								? startResult.lifecycleAdmission.observationToken
								: undefined,
						retainedLifecycle:
							startResult.lifecycleAdmission.status === 'retained'
								? startResult.lifecycleAdmission
								: undefined,
					}),
				onNativeStartRejected: () => {
					stopThinking({ reopenModal: true });
					onClose({
						remixLifecycleEndReason: 'cancelled',
						observationToken:
							startResult.lifecycleAdmission.status === 'retained'
								? startResult.lifecycleAdmission.observationToken
								: undefined,
					});
				},
			});
		},
		[
			adfDocument,
			handleOptionSelect,
			isLifecycleTreatmentEnabled,
			loadAdfDocument,
			onAdmitRemixLifecycle,
			onClose,
			onRemixFlowActiveChange,
			preparePageRemixRequest,
			stopThinking,
		],
	);

	const shouldShowExistingRemixTypeMenu =
		isThinking ||
		!UNSAFE_expValEqualsNoExposure('cc-ce-maui-remix-menu-multivariant', 'cohort', 'variant2');

	return (
		<>
			{isLifecycleTreatmentEnabled && machineLifecycle?.remixId !== undefined && (
				<PageRemixLifecycleNavigationGuard
					cancelActiveRemixRequest={cancelActiveRemixRequest}
					currentContentId={resolvedContentId}
					onClose={onClose}
					originContentId={remixOriginContentIdRef.current}
					stopThinking={stopThinking}
				/>
			)}
			{UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-remix-menu-multivariant',
				'cohort',
				'variant2',
			) && (
				<PageRemixSubmitListener
					currentContentId={resolvedContentId}
					hasGenerationStarted={isThinking}
					onAcceptSubmit={handleVariantTwoPageRemixRequest}
					onDisarm={disarmIdlePageRemix}
					originContentIdRef={remixOriginContentIdRef}
					isRemixLifecycleTreatmentEnabled={isRemixLifecycleTreatmentEnabled}
				/>
			)}
			{shouldShowExistingRemixTypeMenu && (
				<RemixContainer
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					showLabsLozenge={!fg('cc_maui_ga_labs_removal')}
					onClose={handleClose}
					onOptionSelect={handlePickerOptionSelect}
					onRemixFlowActiveChange={onRemixFlowActiveChange}
					hideThinkingBar={isPickerThinkingBarSkipped}
					isEphemeralViewVisible={isEphemeralViewVisible}
					skipThinkingTypes={getPageRemixSkipThinkingTypes()}
					options={PAGE_REMIX_OPTIONS}
					hiddenOptions={hiddenOptions}
					subtitle={remixOverlayMessages.pageRemixSubtitle}
					cascadingMenuHeader={
						showCascadingHeader
							? {
									title: remixOverlayMessages.heading,
									subtitle: remixOverlayMessages.pageRemixSubtitle,
								}
							: undefined
					}
					presentation={presentation}
					trigger={trigger}
					popupPlacement={popupPlacement}
					popupOffset={popupOffset}
					isPopupOpen={isPopupOpen}
					isOptionSelectBlocked={isRemixStartBlocked}
					popupRepositionKey={popupRepositionKey}
					showProgressBar={showProgressBar}
					isComplete={isComplete}
					onCompleteAnimationFinished={onCompleteAnimationFinished}
					isThinking={
						isPickerThinkingControlled
							? isThinking
							: isThinking &&
								  UNSAFE_expValEqualsNoExposure(
										'cc-ce-maui-remix-menu-multivariant',
										'cohort',
										'variant2',
								  )
								? true
								: undefined
					}
				/>
			)}
		</>
	);
};

const PageRemixLifecycleTreatmentModal = (
	props: PageRemixModalProps & {
		onAdmitRetainedRemixLifecycle: NonNullable<
			PageRemixModalProps['onAdmitRetainedRemixLifecycle']
		>;
	},
): React.JSX.Element => {
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const read = useObjectSidebarRemixLifecycle();
	const readRef = useRef(read);
	readRef.current = read;
	const [retainedFlow, setRetainedFlow] = useState<
		| {
				lifecycle: RetainedObjectSidebarRemixLifecycle;
				observationToken?: symbol;
		  }
		| undefined
	>();
	const retainedFlowRef = useRef(retainedFlow);
	const reportedMismatchRemixIdRef = useRef<string | undefined>();
	const [matchedRemixId, setMatchedRemixId] = useState<string | undefined>();
	const {
		onAdmitRetainedRemixLifecycle: admitRetainedRemixLifecycle,
		onClose: closeTreatmentModal,
	} = props;
	// The owner swaps its close handler once a lifecycle is retained, and admission happens inside
	// the same async submit that later hands off. Delegate to the handler current at call time so
	// the in-flight flow releases the lifecycle it admitted instead of only resetting the UI.
	const closeTreatmentModalRef = useRef(closeTreatmentModal);
	closeTreatmentModalRef.current = closeTreatmentModal;
	const onClose = useCallback(
		(options?: PageRemixCloseOptions) => {
			const currentFlow = retainedFlowRef.current;
			if (
				options &&
				Object.prototype.hasOwnProperty.call(options, 'observationToken') &&
				options.observationToken !== currentFlow?.observationToken
			) {
				if (options.remixLifecycleEndReason) {
					reportLifecycleDiagnostic('observed', {
						operation: 'duplicateTerminalAttempt',
						terminalReason: options.remixLifecycleEndReason,
					});
				}
				return;
			}
			if (options?.preserveRemixLifecycle) {
				closeTreatmentModalRef.current(options);
				return;
			}

			if (currentFlow) {
				retainedFlowRef.current = undefined;
				setRetainedFlow(undefined);
			}
			closeTreatmentModalRef.current(options);
			if (currentFlow && readRef.current.remixId === currentFlow.lifecycle.remixId) {
				readRef.current.dismissTerminal();
			}
		},
		[reportLifecycleDiagnostic],
	);
	const onAdmitRetainedRemixLifecycle = useCallback(
		(request: PageRemixLifecycleAdmissionRequest) => {
			const outcome = admitRetainedRemixLifecycle(request);
			if (outcome.status === 'retained') {
				const nextFlow = {
					lifecycle: outcome.lifecycle,
					observationToken: outcome.observationToken,
				};
				retainedFlowRef.current = nextFlow;
				setRetainedFlow(nextFlow);
			}
			return outcome;
		},
		[admitRetainedRemixLifecycle],
	);
	const retainedLifecycle = retainedFlow?.lifecycle;
	const isMatch =
		retainedLifecycle !== undefined &&
		read.hasMachineAuthority &&
		read.remixId === retainedLifecycle.remixId;
	const isMachineAuthority = retainedLifecycle !== undefined || read.hasMachineAuthority;
	const machineRemixId = retainedLifecycle?.remixId;
	const machineConversationId = retainedLifecycle?.conversationId;
	const machineObservationToken = retainedFlow?.observationToken;
	const machinePhase = isMatch ? read.phase : 'inactive';
	const machineTerminalPhase = isMatch ? read.terminalPhase : undefined;
	const machineMismatched = retainedLifecycle !== undefined && !isMatch;
	if (isMatch && matchedRemixId !== retainedLifecycle.remixId) {
		setMatchedRemixId(retainedLifecycle.remixId);
	}
	// A flow the read matched before and now reads as empty was released by the owner (e.g. the Rovo
	// panel closed mid-start), not misread. A read for another remix is still a mismatch.
	const machineReleasedByOwner =
		machineMismatched && read.remixId === undefined && matchedRemixId === retainedLifecycle.remixId;
	const machineLifecycle = useMemo(
		() =>
			isMachineAuthority
				? {
						remixId: machineRemixId,
						conversationId: machineConversationId,
						observationToken: machineObservationToken,
						phase: machinePhase,
						terminalPhase: machineTerminalPhase,
						isMismatched: machineMismatched,
						isReleasedByOwner: machineReleasedByOwner,
					}
				: undefined,
		[
			isMachineAuthority,
			machineRemixId,
			machineConversationId,
			machineObservationToken,
			machinePhase,
			machineTerminalPhase,
			machineMismatched,
			machineReleasedByOwner,
		],
	);
	useEffect(() => {
		if (
			retainedLifecycle &&
			!isMatch &&
			!machineReleasedByOwner &&
			reportedMismatchRemixIdRef.current !== retainedLifecycle.remixId
		) {
			reportedMismatchRemixIdRef.current = retainedLifecycle.remixId;
			reportLifecycleDiagnostic('failed', { operation: 'machineReadMismatch' });
		}
	}, [isMatch, machineReleasedByOwner, reportLifecycleDiagnostic, retainedLifecycle]);
	const onPageRemixStartRejected = useCallback(
		(reason: ObjectSidebarPageRemixStartRejectionReason, entryPath: PageRemixStartEntryPath) => {
			reportLifecycleDiagnostic('rejected', {
				operation: 'preparePageRemixStart',
				entryPath,
				rejectionReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);
	const onRemixHandoffIdentityMismatch = useCallback(
		(entryPath: PageRemixStartEntryPath) => {
			reportLifecycleDiagnostic('failed', {
				operation: 'validateAdmissionIdentity',
				entryPath,
			});
		},
		[reportLifecycleDiagnostic],
	);

	return (
		<PageRemixModalImplementation
			{...props}
			onClose={onClose}
			onAdmitRetainedRemixLifecycle={onAdmitRetainedRemixLifecycle}
			onPageRemixStartRejected={onPageRemixStartRejected}
			onRemixHandoffIdentityMismatch={onRemixHandoffIdentityMismatch}
			shouldWaitForRemixHandoffBeforeClose={
				props.shouldWaitForRemixHandoffBeforeClose || retainedLifecycle !== undefined
			}
			machineLifecycle={machineLifecycle}
			isLifecycleTreatmentEnabled
		/>
	);
};

export const PageRemixModal = (props: PageRemixModalProps): React.JSX.Element => {
	if (props.onAdmitRetainedRemixLifecycle && props.isRemixLifecycleTreatmentEnabled) {
		return (
			<PageRemixLifecycleTreatmentModal
				{...props}
				onAdmitRetainedRemixLifecycle={props.onAdmitRetainedRemixLifecycle}
			/>
		);
	}

	return <PageRemixModalImplementation {...props} isLifecycleTreatmentEnabled={false} />;
};

export type PageRemixSubmitOwnerProps = {
	loadAdfDocument?: () => Promise<DocNode | null>;
	onOpenSidebarConversation?: (conversationId: string) => void;
	onClose?: (options?: PageRemixCloseOptions) => void;
	onStopThinking?: (options?: { reopenModal?: boolean }) => void;
	onAdmitRemixLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	onAdmitRetainedRemixLifecycle?: (
		request: PageRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	onObserveLegacyPhase?: PageRemixModalProps['onObserveLegacyPhase'];
	isRemixLifecycleTreatmentEnabled?: boolean;
	shouldWaitForRemixHandoffBeforeClose?: boolean;
	prefetchedSpaceHiddenOptions?: readonly RemixHiddenOptionKind[];
};

export const PageRemixSubmitOwner = ({
	loadAdfDocument,
	onOpenSidebarConversation,
	onClose = () => {},
	onStopThinking = () => {},
	onAdmitRemixLifecycle,
	onAdmitRetainedRemixLifecycle,
	onObserveLegacyPhase,
	isRemixLifecycleTreatmentEnabled,
	shouldWaitForRemixHandoffBeforeClose,
	prefetchedSpaceHiddenOptions,
}: PageRemixSubmitOwnerProps): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const [pageContentId] = usePageContentId();

	return (
		<ConfluenceConversationAssistant>
			<PageRemixModal
				loadAdfDocument={loadAdfDocument}
				currentContentId={pageContentId}
				onClose={onClose}
				onStopThinking={onStopThinking}
				isEphemeralViewVisible={showSmartCreationModal}
				onOpenSidebarConversation={onOpenSidebarConversation}
				onAdmitRemixLifecycle={onAdmitRemixLifecycle}
				onAdmitRetainedRemixLifecycle={onAdmitRetainedRemixLifecycle}
				onObserveLegacyPhase={onObserveLegacyPhase}
				isRemixLifecycleTreatmentEnabled={isRemixLifecycleTreatmentEnabled}
				shouldWaitForRemixHandoffBeforeClose={shouldWaitForRemixHandoffBeforeClose}
				prefetchedSpaceHiddenOptions={prefetchedSpaceHiddenOptions}
			/>
		</ConfluenceConversationAssistant>
	);
};

export const EditorPageRemixModal = (
	props: Extract<RemixContentComponentProps, { remixType: 'object' }> & {
		prefetchedAttachmentHiddenOptions?: readonly RemixHiddenOptionKind[];
		prefetchedSpaceHiddenOptions?: readonly RemixHiddenOptionKind[];
	},
): React.JSX.Element => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const interactionSource = props.remixModalMetadata?.interactionSource;

	return (
		<PageRemixModal
			adfDocument={props.adfDocument ?? { type: 'doc', version: 1, content: [] }}
			onClose={props.handleModalClose}
			onStopThinking={props.onStopThinking}
			isEphemeralViewVisible={showSmartCreationModal}
			interactionSource={interactionSource}
			presentation="modal"
			prefetchedAttachmentHiddenOptions={props.prefetchedAttachmentHiddenOptions}
			prefetchedSpaceHiddenOptions={props.prefetchedSpaceHiddenOptions}
		/>
	);
};
